import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const FooterWrapper = styled.footer`
  background: linear-gradient(180deg, #000000ff 0%, #000000ff 40%, #000000 100%);
  border-top: 1px solid rgba(0, 245, 255, 0.12);
  padding: 6rem 2rem 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #00f5ff, transparent);
    opacity: 0.5;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0; top: 50%;
    background: radial-gradient(circle at 50% 100%, rgba(0, 0, 0, 0.6), transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  @media (max-width: 768px) {
    padding: 5rem 1.5rem 2.5rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1fr;
  gap: 2rem;
  align-items: start;
  position: relative;
  z-index: 1;

  @media (max-width: 992px) {
    grid-template-columns: 1.2fr 1fr 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
    text-align: center;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Logo = styled(motion.div)`
  font-size: 2.1rem;
  font-weight: 800;
  background: linear-gradient(135deg, #a0a0a0ff, #00f5ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -1.2px;
`;

const Tagline = styled.p`
  font-size: 0.95rem;
  color: #888;
  margin: 0;
  opacity: 0.8;
  line-height: 1.6;
`;

const NavColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

const NavTitle = styled.h4`
  font-size: 0.95rem;
  font-weight: 600;
  color: #cccccc;
  margin: 0 0 0.5rem;
  opacity: 0.8;
  letter-spacing: 0.5px;
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const NavLink = styled(motion.a)`
  font-size: 0.875rem;
  color: #999;
  text-decoration: none;
  opacity: 0.8;
  transition: all 0.25s ease;
  display: inline-block;

  &:hover {
    color: #00f5ff;
    opacity: 1;
    transform: translateX(2px);
  }

  @media (max-width: 768px) {
    &:hover {
      transform: none;
    }
  }
`;

const SocialColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 0.75rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialLink = styled(motion.a)`
  width: 36px;
  height: 36px;
  background: rgba(0, 245, 255, 0.06);
  border: 1px solid rgba(0, 245, 255, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00f5ff;
  opacity: 0.8;
  transition: all 0.3s ease;

  &:hover {
    background: #00f5ff;
    color: #0a0a0a;
    opacity: 1;
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(0, 245, 255, 0.25);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BottomBar = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(0, 245, 255, 0.1);
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1rem;
  align-items: center;
  font-size: 0.8rem;
  color: #555;
  opacity: 0.8;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 0.75rem;
  }
`;

const Copyright = styled.div`
  text-align: left;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;

  @media (max-width: 768px) {
    order: -1;
    justify-content: center;
  }
`;

const LegalLink = styled.a`
  color: #666;
  text-decoration: none;
  font-size: 0.8rem;
  opacity: 0.8;
  transition: all 0.25s ease;

  &:hover {
    color: #00f5ff;
    opacity: 1;
  }
`;

const Footer = () => {
  const { t } = useTranslation();

  const fadeUp = {
    initial: { opacity: 0, y: 15 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 }
  };

  return (
    <FooterWrapper>
      <Container>
        {/* Логотип и слоган */}
        <LeftColumn>
          <Logo {...fadeUp} transition={{ delay: 0.1 }}>
            Plano
          </Logo>
          <Tagline {...fadeUp} transition={{ delay: 0.2 }}>
            {t('footer_tagline')}
          </Tagline>
        </LeftColumn>

        {/* Product */}
        <NavColumn>
          <NavTitle>{t('footer_product')}</NavTitle>
          <NavList>
            <motion.li {...fadeUp} transition={{ delay: 0.1 }}>
              <NavLink href="/features">{t('footer_features')}</NavLink>
            </motion.li>
            <motion.li {...fadeUp} transition={{ delay: 0.15 }}>
              <NavLink href="/pricing">{t('footer_pricing')}</NavLink>
            </motion.li>
            <motion.li {...fadeUp} transition={{ delay: 0.2 }}>
              <NavLink href="/privatpolicy">{t('footer_roadmap')}</NavLink>
            </motion.li>
            <motion.li {...fadeUp} transition={{ delay: 0.25 }}>
              <NavLink href="/cookiepolicy">{t('footer_changelog')}</NavLink>
            </motion.li>
          </NavList>
        </NavColumn>

        {/* Company */}
        <NavColumn>
          <NavTitle>{t('footer_company')}</NavTitle>
          <NavList>
            <motion.li {...fadeUp} transition={{ delay: 0.1 }}>
              <NavLink href="/about">{t('footer_about')}</NavLink>
            </motion.li>
            <motion.li {...fadeUp} transition={{ delay: 0.15 }}>
              <NavLink href="/team">{t('footer_team')}</NavLink>
            </motion.li>
            <motion.li {...fadeUp} transition={{ delay: 0.2 }}>
              <NavLink href="/contactus">{t('footer_contact')}</NavLink>
            </motion.li>
          </NavList>
        </NavColumn>

        {/* Соцсети */}
        <SocialColumn>
          <NavTitle>{t('footer_follow')}</NavTitle>
          <SocialLinks>
            <SocialLink
              href="https://www.instagram.com/plano.ai/"
              target="_blank"
              rel="noopener noreferrer"
              {...fadeUp}
              transition={{ delay: 0.1 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <circle cx="12" cy="12" r="5" />
                <line x1="18" y1="6" x2="18.01" y2="6" />
              </svg>
            </SocialLink>
            <SocialLink
              href="https://www.linkedin.com/company/visiondo"
              target="_blank"
              rel="noopener noreferrer"
              {...fadeUp}
              transition={{ delay: 0.15 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451c.979 0 1.771-.773 1.771-1.729V1.729C24 .774 23.208 0 22.225 0z"/>
              </svg>
            </SocialLink>
          </SocialLinks>
        </SocialColumn>
      </Container>

      {/* Нижняя полоса */}
      <BottomBar>
        <Copyright>
          {t('footer_copyright')}
        </Copyright>

        <LegalLinks>
          <LegalLink href="/privatpolicy">{t('footer_privacy')}</LegalLink>
          <LegalLink href="/termofservice">{t('footer_terms')}</LegalLink>
          <LegalLink href="/cookiepolicy">{t('footer_cookies')}</LegalLink>
        </LegalLinks>

        <div />
      </BottomBar>
    </FooterWrapper>
  );
};

export default Footer;